import React from "react";
import AlertsDashboard from "./AlertsDashboard";
import AlertsList from "./AlertsList";
import AlertDetail from "./AlertDetail";
import ScamActivityMap from "./ScamActivityMap";
import RefreshButton from "../common/RefreshButton";

/**
 * AlertsIntelligenceHubMobile - Mobile shell for the Alerts Intelligence Hub.
 *
 * Renders a mobile-optimised header:
 *  - Back button row (full-width prominent button, when not on dashboard)
 *  - Title row with search icon toggle on the right + refresh
 *  - Full-width expanded search bar below the title row
 *  - Subtitle is hidden to conserve vertical space
 *
 * Passes `isMobile={true}` to AlertsDashboard (compact 2×2 stat grid) and
 * AlertDetail (sticky action bar, full-width buttons).
 *
 * All state and event handlers are owned by AlertsIntelligenceHub.js and
 * passed in as props — this component is a pure presentation layer.
 */
function AlertsIntelligenceHubMobile({
  // View
  currentView,
  selectedCategory,
  categories,
  filterLabel,
  // Search
  searchExpanded,
  searchInput,
  activeSearchTerm,
  // Data
  dashboardData,
  dashboardLoading,
  dashboardError,
  alerts,
  alertsLoading,
  alertsPagination,
  selectedAlert,
  alertDetail,
  detailLoading,
  // Handlers
  goBack,
  handleViewAlert,
  handleCategorySelect,
  handleMarkReviewed,
  handleStartTraining,
  handleOpenShareModal,
  handleDismissAlert,
  handleRefresh,
  loadMoreAlerts,
  navigateTo,
  toggleSearch,
  handleSearch,
  handleSearchKeyPress,
  handleSearchBlur,
  clearSearch,
  setSearchInput,
  searchContainerRef,
}) {
  const pageTitle =
    currentView === "dashboard"
      ? "Alerts Hub"
      : currentView === "scammap"
        ? "Scam Activity Near You"
        : currentView === "detail"
          ? "Alert Details"
          : currentView === "list"
            ? filterLabel || "All Alerts"
            : currentView === "category" && selectedCategory
              ? categories.find((c) => c.name === selectedCategory)
                  ?.displayName || selectedCategory
              : "";

  // Search is not shown on the detail view (no alert search while reading one)
  const showSearch = currentView !== "detail";

  return (
    <div className="common-container aih-container">
      {/* ── Mobile Header — hidden for scammap (that view owns its header) ── */}
      {currentView !== "scammap" && <div className="common-header">
        <div className="aih-header-wrapper">
          {/* Row 1: Back button (when not on dashboard) — full-width on mobile */}
          {currentView !== "dashboard" && (
            <div className="aih-header-back-row">
              <button
                className="aih-back-button aih-back-button-mobile"
                onClick={goBack}
                aria-label="Go back"
              >
                <span className="back-arrow">←</span>
                <span className="back-text">
                  {currentView === "detail"
                    ? "Back to Alerts"
                    : "Back to Alerts Hub"}
                </span>
              </button>
            </div>
          )}

          {/* Row 2: Title + search icon + refresh */}
          <div className="aih-header-content aih-header-content-mobile">
            <div className="aih-title-section">
              <h1 className="main-title">{pageTitle}</h1>
            </div>

            <div className="aih-header-actions aih-header-actions-mobile">
              {/* Search icon toggle (hidden on detail view or when expanded) */}
              {showSearch && !searchExpanded && !activeSearchTerm && (
                <button
                  className="aih-search-icon-btn"
                  onClick={toggleSearch}
                  aria-label="Open search"
                  title="Search alerts"
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                </button>
              )}

              <RefreshButton onClick={handleRefresh} title="Refresh data" />
            </div>
          </div>

          {/* Row 3: Full-width search bar (when expanded) */}
          {showSearch && (searchExpanded || activeSearchTerm) && (
            <div
              className="aih-search-container aih-search-container-mobile"
              ref={searchContainerRef}
            >
              <div className="aih-search-row">
                <input
                  type="text"
                  className="aih-search-input"
                  placeholder="Search alerts..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleSearchKeyPress}
                />
                <button
                  className="aih-search-submit"
                  onClick={handleSearch}
                  aria-label="Search"
                >
                  Search
                </button>
              </div>
              {activeSearchTerm && (
                <div className="aih-search-criteria">
                  <span className="aih-search-criteria-label">Search:</span>
                  <span className="aih-search-criteria-term">
                    {activeSearchTerm}
                  </span>
                  <button
                    className="aih-search-criteria-clear"
                    onClick={clearSearch}
                    aria-label="Clear search"
                    title="Clear search and show all alerts"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>}

      {/* ── Content Panel ── */}
      <div className="aih-content-panel">
        {currentView === "dashboard" && (
          <AlertsDashboard
            data={dashboardData}
            loading={dashboardLoading}
            error={dashboardError}
            categories={categories}
            isMobile={true}
            onViewAlert={handleViewAlert}
            onCategorySelect={handleCategorySelect}
            onRefresh={handleRefresh}
            onViewAllAlerts={() => navigateTo("list")}
            onFilterCritical={() =>
              navigateTo("list", {
                minThreatLevel: 8,
                maxThreatLevel: 10,
                filterLabel: "Critical Alerts",
              })
            }
            onFilterUnreviewed={() =>
              navigateTo("list", { unreviewedOnly: true })
            }
            onNavigateToScamMap={() => navigateTo("scammap")}
          />
        )}

        {currentView === "scammap" && (
          <ScamActivityMap goBack={goBack} />
        )}

        {(currentView === "list" || currentView === "category") && (
          <div className="aih-list-view">
            <AlertsList
              alerts={alerts}
              loading={alertsLoading}
              pagination={alertsPagination}
              onViewAlert={handleViewAlert}
              onMarkReviewed={handleMarkReviewed}
              onShare={handleOpenShareModal}
              onDismiss={handleDismissAlert}
              onLoadMore={loadMoreAlerts}
              emptyMessage={
                currentView === "category"
                  ? "No alerts found in this category."
                  : "No alerts available."
              }
            />
          </div>
        )}

        {currentView === "detail" && (
          <AlertDetail
            alertId={selectedAlert}
            alertData={alertDetail}
            loading={detailLoading}
            isMobile={true}
            onMarkReviewed={handleMarkReviewed}
            onStartTraining={handleStartTraining}
            onShare={handleOpenShareModal}
            onViewRelated={handleViewAlert}
            onBack={goBack}
          />
        )}
      </div>
    </div>
  );
}

export default AlertsIntelligenceHubMobile;
